import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { 
  Video, Users, MessageSquare, FileText, Mic, MicOff, VideoOff,
  Phone, PhoneOff, UserPlus, Settings, Maximize, Minimize,
  Send, Clock, AlertTriangle, CheckCircle2, RefreshCw,
  Camera, ScreenShare, PanelRightOpen, ChevronRight
} from 'lucide-react';
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

const AdvancedTelehealthInterface = () => {
  const [activeRooms, setActiveRooms] = useState([]);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [inCall, setInCall] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [liveNotes, setLiveNotes] = useState('');
  const [showNotes, setShowNotes] = useState(true);
  const [virtualKnock, setVirtualKnock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mediaState, setMediaState] = useState({ video: true, audio: true, screen: false });
  const { toast } = useToast();

  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchActiveRooms = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/phase38-44/telehealth/active-rooms`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setActiveRooms(data);
      }
    } catch (err) {
      console.error('Error fetching rooms:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchActiveRooms();
    // Poll for updates
    const interval = setInterval(fetchActiveRooms, 10000);
    return () => clearInterval(interval);
  }, [fetchActiveRooms]);

  // Initiate Virtual Knock (Emergency Video)
  const initiateVirtualKnock = async (patientId, reason = 'Need immediate assistance') => {
    try {
      const res = await fetch(`${API}/api/phase38-44/telehealth/virtual-knock?patient_id=${patientId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      });

      if (res.ok) {
        const data = await res.json();
        setVirtualKnock(data);
        toast({
          title: "Connecting...",
          description: "A nurse is being notified and will join shortly.",
        });
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to initiate virtual knock", variant: "destructive" });
    }
  };

  // Answer Virtual Knock
  const answerVirtualKnock = async (knockId) => {
    try {
      const res = await fetch(`${API}/api/phase38-44/telehealth/virtual-knock/${knockId}/answer`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setCurrentRoom({ id: data.room_id, type: 'emergency' });
        setInCall(true);
        toast({ title: "Connected", description: "You're now connected to the patient." });
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to answer call", variant: "destructive" });
    }
  };

  // Join Video Room
  const joinRoom = async (roomId) => {
    try {
      const res = await fetch(`${API}/api/phase38-44/telehealth/room/${roomId}/join`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setCurrentRoom(data);
        setParticipants(data.current_participants || []);
        setInCall(true);
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to join room", variant: "destructive" });
    }
  };

  // Leave Video Room
  const leaveRoom = async () => {
    if (!currentRoom) return;

    try {
      await fetch(`${API}/api/phase38-44/telehealth/room/${currentRoom.room_id || currentRoom.id}/leave`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setCurrentRoom(null);
      setInCall(false);
      setParticipants([]);
      setChatMessages([]);
      setLiveNotes('');
    } catch (err) {
      console.error('Error leaving room:', err);
    }
  };

  // Add Participant (Family Room feature)
  const addParticipant = async (participantData) => {
    if (!currentRoom) return;

    try {
      const res = await fetch(`${API}/api/phase38-44/telehealth/room/${currentRoom.room_id}/add-participant`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(participantData)
      });

      if (res.ok) {
        toast({ title: "Invitation Sent", description: `${participantData.name} has been invited to join.` });
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to add participant", variant: "destructive" });
    }
  };

  // Toggle Media
  const toggleMedia = (type) => {
    setMediaState(prev => ({ ...prev, [type]: !prev[type] }));
  };

  // Send Chat Message (Side-Chat for clinicians)
  const sendChatMessage = () => {
    if (!newMessage.trim()) return;

    const message = {
      id: Date.now(),
      sender: user.name,
      role: user.role,
      content: newMessage,
      timestamp: new Date().toISOString(),
      isPrivate: true // Clinician side-chat
    };

    setChatMessages(prev => [...prev, message]);
    setNewMessage('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  // In-Call View
  if (inCall && currentRoom) {
    return (
      <div className="h-screen bg-gray-900 flex flex-col">
        {/* Video Header */}
        <div className="bg-gray-800 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Badge className={currentRoom.type === 'emergency' ? 'bg-red-500' : 'bg-green-500'}>
              {currentRoom.type === 'emergency' ? 'EMERGENCY' : 'Active'}
            </Badge>
            <span className="text-white font-medium">
              {currentRoom.title || 'Video Session'}
            </span>
          </div>
          <div className="flex items-center gap-2 text-white text-sm">
            <Users className="h-4 w-4" />
            <span>{participants.length} participants</span>
            <span className="ml-4 flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span>00:00</span>
            </span>
          </div>
        </div>

        {/* Main Content - Split View */}
        <div className="flex-1 flex">
          {/* Video Area */}
          <div className={`${showNotes ? 'w-2/3' : 'w-full'} bg-gray-900 relative p-4`}>
            {/* Main Video Grid */}
            <div className="grid grid-cols-2 gap-4 h-full">
              {/* Self View */}
              <div className="bg-gray-800 rounded-xl flex items-center justify-center relative">
                <div className="bg-gray-700 w-full h-full rounded-xl flex items-center justify-center">
                  {mediaState.video ? (
                    <div className="text-gray-400">Camera Preview</div>
                  ) : (
                    <VideoOff className="h-12 w-12 text-gray-500" />
                  )}
                </div>
                <div className="absolute bottom-3 left-3 bg-black/50 px-2 py-1 rounded text-white text-sm">
                  You
                </div>
              </div>

              {/* Remote Participants */}
              {participants.slice(0, 5).map((p, i) => (
                <div key={i} className="bg-gray-800 rounded-xl flex items-center justify-center relative">
                  <div className="bg-gray-700 w-full h-full rounded-xl flex items-center justify-center">
                    <Users className="h-12 w-12 text-gray-500" />
                  </div>
                  <div className="absolute bottom-3 left-3 bg-black/50 px-2 py-1 rounded text-white text-sm">
                    {p.name} ({p.role})
                  </div>
                </div>
              ))}

              {/* Add Participant Slot (Family Room - up to 6) */}
              {participants.length < 6 && (
                <div 
                  className="bg-gray-800/50 border-2 border-dashed border-gray-600 rounded-xl flex items-center justify-center cursor-pointer hover:border-teal-500 transition-colors"
                  onClick={() => {
                    const name = prompt('Enter participant name:');
                    const email = prompt('Enter participant email:');
                    if (name && email) {
                      addParticipant({ name, email });
                    }
                  }}
                >
                  <div className="text-center text-gray-500">
                    <UserPlus className="h-8 w-8 mx-auto mb-2" />
                    <span className="text-sm">Add Family Member</span>
                  </div>
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex items-center gap-3">
              <Button
                variant={mediaState.audio ? "default" : "destructive"}
                size="lg"
                className="rounded-full h-14 w-14"
                onClick={() => toggleMedia('audio')}
              >
                {mediaState.audio ? <Mic className="h-6 w-6" /> : <MicOff className="h-6 w-6" />}
              </Button>
              <Button
                variant={mediaState.video ? "default" : "destructive"}
                size="lg"
                className="rounded-full h-14 w-14"
                onClick={() => toggleMedia('video')}
              >
                {mediaState.video ? <Camera className="h-6 w-6" /> : <VideoOff className="h-6 w-6" />}
              </Button>
              <Button
                variant={mediaState.screen ? "secondary" : "outline"}
                size="lg"
                className="rounded-full h-14 w-14"
                onClick={() => toggleMedia('screen')}
              >
                <ScreenShare className="h-6 w-6" />
              </Button>
              <Button
                variant="destructive"
                size="lg"
                className="rounded-full h-14 w-14"
                onClick={leaveRoom}
              >
                <PhoneOff className="h-6 w-6" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="rounded-full h-14 w-14"
                onClick={() => setShowNotes(!showNotes)}
              >
                <PanelRightOpen className="h-6 w-6" />
              </Button>
            </div>
          </div>

          {/* Live Encounter Notes Panel (Context-Aware) */}
          {showNotes && (
            <div className="w-1/3 bg-white border-l flex flex-col">
              <Tabs defaultValue="notes" className="flex-1 flex flex-col">
                <TabsList className="grid grid-cols-2 m-2">
                  <TabsTrigger value="notes">Live Notes</TabsTrigger>
                  <TabsTrigger value="chat">
                    Side Chat
                    {chatMessages.filter(m => m.isPrivate).length > 0 && (
                      <Badge className="ml-1 bg-blue-500 h-5 w-5 p-0 justify-center">
                        {chatMessages.filter(m => m.isPrivate).length}
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="notes" className="flex-1 p-4 flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold">Encounter Documentation</h3>
                    <Badge variant="outline" className="text-green-600">
                      Auto-saving
                    </Badge>
                  </div>
                  <Textarea
                    value={liveNotes}
                    onChange={(e) => setLiveNotes(e.target.value)}
                    placeholder="Document the encounter in real-time..."
                    className="flex-1 resize-none"
                  />
                  <div className="mt-3 flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1">
                      <FileText className="h-4 w-4 mr-1" />
                      Templates
                    </Button>
                    <Button size="sm" className="flex-1 bg-teal-600 hover:bg-teal-700">
                      Save to EHR
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="chat" className="flex-1 p-4 flex flex-col">
                  <div className="flex-1 overflow-y-auto space-y-3 mb-3">
                    {chatMessages.filter(m => m.isPrivate).map((msg, i) => (
                      <div key={i} className={`p-2 rounded-lg ${
                        msg.sender === user.name ? 'bg-teal-100 ml-6' : 'bg-gray-100 mr-6'
                      }`}>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                          <span className="font-medium">{msg.sender}</span>
                          <Badge variant="outline" className="text-xs">{msg.role}</Badge>
                        </div>
                        <p className="text-sm">{msg.content}</p>
                      </div>
                    ))}
                    {chatMessages.filter(m => m.isPrivate).length === 0 && (
                      <p className="text-center text-gray-500 text-sm py-8">
                        Private chat for clinicians only.<br />
                        Patients and family cannot see these messages.
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Private message to care team..."
                      onKeyDown={(e) => e.key === 'Enter' && sendChatMessage()}
                    />
                    <Button onClick={sendChatMessage}>
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Lobby View
  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Video className="h-7 w-7 text-teal-600" />
            Advanced Telehealth Center
          </h1>
          <p className="text-gray-600">Context-Aware Video Visits with Live Documentation</p>
        </div>
        <Button 
          className="bg-red-500 hover:bg-red-600"
          onClick={() => initiateVirtualKnock('demo-patient')}
        >
          <Phone className="h-4 w-4 mr-2" />
          Virtual Knock (Emergency)
        </Button>
      </div>

      {/* Virtual Knock Alert */}
      {virtualKnock && (
        <Card className="border-red-200 bg-red-50 animate-pulse">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-red-100 p-3 rounded-full">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-red-900">Incoming Virtual Knock</h3>
                  <p className="text-sm text-red-700">
                    {virtualKnock.patient_name || 'Patient'} needs immediate assistance
                  </p>
                </div>
              </div>
              <Button 
                className="bg-green-500 hover:bg-green-600"
                onClick={() => answerVirtualKnock(virtualKnock.knock_id)}
              >
                <Phone className="h-4 w-4 mr-2" />
                Answer
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Rooms */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-teal-600" />
            Active & Scheduled Sessions
          </CardTitle>
          <CardDescription>Join or manage video sessions</CardDescription>
        </CardHeader>
        <CardContent>
          {activeRooms.length > 0 ? (
            <div className="space-y-3">
              {activeRooms.map((room, i) => (
                <div 
                  key={i}
                  className="flex items-center justify-between p-4 border rounded-lg hover:border-teal-300 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-full ${
                      room.status === 'active' ? 'bg-green-100' : 
                      room.status === 'waiting' ? 'bg-yellow-100' : 'bg-gray-100'
                    }`}>
                      <Video className={`h-5 w-5 ${
                        room.status === 'active' ? 'text-green-600' :
                        room.status === 'waiting' ? 'text-yellow-600' : 'text-gray-600'
                      }`} />
                    </div>
                    <div>
                      <h4 className="font-medium">{room.title}</h4>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Badge variant="outline">{room.type}</Badge>
                        <span>{room.current_participants?.length || 0} participants</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={
                      room.status === 'active' ? 'bg-green-500' :
                      room.status === 'waiting' ? 'bg-yellow-500' : 'bg-gray-500'
                    }>
                      {room.status}
                    </Badge>
                    <Button onClick={() => joinRoom(room.id)}>
                      Join
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Video className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p>No active sessions</p>
              <p className="text-sm">Start a new session or wait for a virtual knock</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="cursor-pointer hover:border-teal-300 transition-colors">
          <CardContent className="p-6 text-center">
            <Users className="h-10 w-10 mx-auto text-teal-600 mb-3" />
            <h3 className="font-semibold">Family Conference</h3>
            <p className="text-sm text-gray-500 mt-1">Up to 6 remote family members</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-teal-300 transition-colors">
          <CardContent className="p-6 text-center">
            <MessageSquare className="h-10 w-10 mx-auto text-blue-600 mb-3" />
            <h3 className="font-semibold">Care Team Huddle</h3>
            <p className="text-sm text-gray-500 mt-1">Internal team coordination</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-teal-300 transition-colors">
          <CardContent className="p-6 text-center">
            <FileText className="h-10 w-10 mx-auto text-purple-600 mb-3" />
            <h3 className="font-semibold">Scheduled Visit</h3>
            <p className="text-sm text-gray-500 mt-1">Routine telehealth appointment</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdvancedTelehealthInterface;
